import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import AppInput from './AppInput';
import cx from './cx';
import '../../styles/app-ui.scss';

const AppAutocomplete = ({
  value,
  onChange,
  items = [],
  loading = false,
  getItemKey = (item, index) => item?.id ?? index,
  renderItem,
  onSelect,
  placeholder,
  disabled = false,
  required = false,
  size,
  minQueryLength = 2,
  emptyMessage = 'Aucun résultat',
  loadingLabel = 'Recherche…',
  className = '',
  inputClassName = '',
  autoFocus = false,
  onKeyDown,
  onBlur,
  onFocus,
}) => {
  const inputRef = useRef(null);
  const selectingRef = useRef(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [dropdownRect, setDropdownRect] = useState(null);

  const query = String(value ?? '').trim();
  const canSearch = minQueryLength <= 0 || query.length >= minQueryLength;
  const showDropdown = showSuggestions && canSearch && !disabled;
  const showEmpty = showDropdown && !loading && items.length === 0;

  const updateDropdownRect = () => {
    const input = inputRef.current;
    if (!input) {
      setDropdownRect(null);
      return;
    }
    const rect = input.getBoundingClientRect();
    setDropdownRect({
      top: rect.bottom + 2,
      left: rect.left,
      width: rect.width,
    });
  };

  useLayoutEffect(() => {
    if (!showDropdown) {
      setDropdownRect(null);
      return undefined;
    }
    updateDropdownRect();
    window.addEventListener('resize', updateDropdownRect);
    window.addEventListener('scroll', updateDropdownRect, true);
    return () => {
      window.removeEventListener('resize', updateDropdownRect);
      window.removeEventListener('scroll', updateDropdownRect, true);
    };
  }, [showDropdown, items.length, loading, value]);

  useEffect(() => {
    if (autoFocus) {
      setShowSuggestions(true);
    }
  }, [autoFocus]);

  const handleInputChange = (nextValue) => {
    setShowSuggestions(true);
    onChange?.(nextValue);
  };

  const handleSelect = (item) => {
    selectingRef.current = true;
    setShowSuggestions(false);
    onSelect?.(item);
    window.setTimeout(() => {
      selectingRef.current = false;
    }, 200);
  };

  const handleBlur = (event) => {
    window.setTimeout(() => {
      if (selectingRef.current) return;
      setShowSuggestions(false);
      onBlur?.(event);
    }, 150);
  };

  const dropdownStyle = dropdownRect
    ? {
      position: 'fixed',
      top: dropdownRect.top,
      left: dropdownRect.left,
      width: dropdownRect.width,
      zIndex: 2000,
    }
    : null;

  const dropdown = showDropdown && dropdownRect && (
    <>
      {items.length > 0 && (
        <ul className="list-group app-autocomplete__dropdown shadow-sm" style={dropdownStyle}>
          {items.map((item, index) => (
            <li key={getItemKey(item, index)}>
              <button
                type="button"
                className="list-group-item list-group-item-action text-start"
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleSelect(item);
                }}
              >
                {renderItem(item)}
              </button>
            </li>
          ))}
        </ul>
      )}
      {showEmpty && (
        <div
          className="app-autocomplete__dropdown app-autocomplete__empty text-muted"
          style={dropdownStyle}
        >
          {emptyMessage}
        </div>
      )}
    </>
  );

  return (
    <div className={cx('app-autocomplete', 'position-relative', className)}>
      <AppInput
        ref={inputRef}
        type="text"
        size={size}
        className={inputClassName}
        value={value ?? ''}
        onChange={(e) => handleInputChange(e.target.value)}
        onKeyDown={onKeyDown}
        onFocus={(e) => {
          setShowSuggestions(true);
          onFocus?.(e);
        }}
        onBlur={handleBlur}
        placeholder={placeholder}
        autoFocus={autoFocus}
        required={required}
        disabled={disabled}
        autoComplete="off"
      />
      {loading && showDropdown && (
        <span className="app-autocomplete__status text-secondary-light">
          {loadingLabel}
        </span>
      )}
      {dropdown && createPortal(dropdown, document.body)}
    </div>
  );
};

export default AppAutocomplete;
